import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ZoomOut, AlertTriangle } from "lucide-react";

// ── Pathology stages for epithelium (educational only) ──────────────
const PATHOLOGY_STAGE_KEYS = ["normal", "hyperplasia", "adh", "dcis", "invasive"];
const PATHOLOGY_STAGES = {
  normal: {
    title_bg: "Нормален TDLU",
    risk_bg: "Базова линия",
    desc_bg: "Един слой луминални клетки + миоепител + интактна базална мембрана.",
    cellRows: 1, cellsPerRow: 8, nucleusSize: 6, atypia: false, bm: true,
  },
  hyperplasia: {
    title_bg: "Доброкачествена хиперплазия",
    risk_bg: "Леко повишен относителен риск (~1,5×)",
    desc_bg: "Множество слоеве нормални клетки. Базална мембрана интактна, без атипия.",
    cellRows: 2, cellsPerRow: 9, nucleusSize: 6, atypia: false, bm: true,
  },
  adh: {
    title_bg: "ADH (атипична дуктална хиперплазия)",
    risk_bg: "Относителен риск ~4–5× (нужно проследяване)",
    desc_bg: "Многослойност + начало на ядрена атипия. Не запълва изцяло лумена.",
    cellRows: 3, cellsPerRow: 10, nucleusSize: 8, atypia: true, bm: true,
  },
  dcis: {
    title_bg: "DCIS (ductal carcinoma in situ)",
    risk_bg: "Неинвазивно — лечимо при ранно откриване",
    desc_bg: "Атипични клетки запълват изцяло лумена. Базалната мембрана е НЕ нарушена — \"in situ\".",
    cellRows: 4, cellsPerRow: 11, nucleusSize: 10, atypia: true, bm: true, filled: true,
  },
  invasive: {
    title_bg: "Инвазивен карцином",
    risk_bg: "Изисква незабавна медицинска оценка",
    desc_bg: "Атипични клетки преминават през БАЗАЛНАТА МЕМБРАНА в стромата.",
    cellRows: 4, cellsPerRow: 11, nucleusSize: 10, atypia: true, bm: false, filled: true,
  },
};

/**
 * CytologyZoom — modal-style microscopic visualisation that opens when
 * the user clicks a tissue zone in BreastAnatomyVisual. Shows a
 * stylized educational cytology view of:
 *   - epithelium → TDLU acini + ductal lumen + myoepithelial cells + basement membrane
 *   - stroma     → fibroblasts + collagen fibres + ECM
 *   - adipose    → adipocytes + aromatase-positive cells
 *   - immune     → lymphocytes / macrophages in tissue
 *   - vascular   → endothelial cells lining capillaries
 *
 * Pure SVG. No medical claims. Educational labels.
 */

const CYTOLOGY_CONTENT = {
  epithelium: {
    title_bg: "Епител — клетъчна структура",
    title_en: "Epithelium — cellular architecture",
    title_es: "Epitelio — arquitectura celular",
    description_bg: "Tерминална дуктална лобуларна единица (TDLU): луминални епителни клетки + миоепителен слой + базална мембрана. TDLU е основното място, където започват повечето доброкачествени и злокачествени промени на гърдата.",
    description_en: "Terminal Ductal Lobular Unit (TDLU): luminal epithelial cells + myoepithelial layer + basement membrane. The TDLU is the site where most benign and malignant breast changes originate.",
    labels: {
      bg: { luminal: "Луминални\nепителни клетки", myoepithelial: "Миоепителен\nслой", basement: "Базална\nмембрана", lumen: "Луминален\nканал" },
      en: { luminal: "Luminal epithelial cells", myoepithelial: "Myoepithelial layer", basement: "Basement membrane", lumen: "Ductal lumen" },
      es: { luminal: "Células epiteliales luminales", myoepithelial: "Capa mioepitelial", basement: "Membrana basal", lumen: "Lumen ductal" },
    },
  },
  stroma: {
    title_bg: "Строма — ECM и фибробласти",
    title_en: "Stroma — ECM and fibroblasts",
    title_es: "Estroma — ECM y fibroblastos",
    description_bg: "Стромалните фибробласти произвеждат колагенни влакна (COL1A1 и COL3A1). LOX ензимът кръстосано свързва влакната, увеличавайки тъканната твърдост — характерно за плътните гърди.",
    description_en: "Stromal fibroblasts produce collagen fibres (COL1A1 and COL3A1). LOX enzyme cross-links them, increasing tissue stiffness — characteristic of dense breasts.",
    labels: {
      bg: { fibro: "Фибробласт", col1: "COL1A1\n(тип I)", col3: "COL3A1\n(тип III)", ecm: "ECM матрикс" },
      en: { fibro: "Fibroblast", col1: "COL1A1 (type I)", col3: "COL3A1 (type III)", ecm: "ECM matrix" },
      es: { fibro: "Fibroblasto", col1: "COL1A1 (tipo I)", col3: "COL3A1 (tipo III)", ecm: "Matriz ECM" },
    },
  },
  adipose: {
    title_bg: "Мастна тъкан — адипоцити & ароматаза",
    title_en: "Adipose tissue — adipocytes & aromatase",
    title_es: "Tejido adiposo — adipocitos & aromatasa",
    description_bg: "Адипоцитите в гърдата експресират ароматаза (CYP19A1) — ензимът, превръщащ андрогените в естрогени. В постменопауза това е основният източник на естроген.",
    description_en: "Breast adipocytes express aromatase (CYP19A1) — the enzyme that converts androgens to estrogens. In post-menopause this is the main source of estrogen.",
    labels: {
      bg: { adipo: "Адипоцит\n(мастна\nклетка)", arom: "Ароматаза\n(CYP19A1)", lipid: "Липидна\nкапка" },
      en: { adipo: "Adipocyte (fat cell)", arom: "Aromatase (CYP19A1)", lipid: "Lipid droplet" },
      es: { adipo: "Adipocito", arom: "Aromatasa (CYP19A1)", lipid: "Gota lipídica" },
    },
  },
  immune: {
    title_bg: "Имунна среда — TIL и макрофаги",
    title_en: "Immune environment — TILs and macrophages",
    title_es: "Entorno inmune — TILs y macrófagos",
    description_bg: "Тъканно-инфилтриращи лимфоцити (TILs) и макрофаги — балансът между про- и анти-туморни клетки формира локалната имунна среда.",
    description_en: "Tissue-infiltrating lymphocytes (TILs) and macrophages — the balance between pro- and anti-tumour cells shapes the local immune environment.",
    labels: {
      bg: { til: "T-лимфоцит\n(TIL)", macro: "Макрофаг", nk: "NK клетка" },
      en: { til: "T-lymphocyte (TIL)", macro: "Macrophage", nk: "NK cell" },
      es: { til: "Linfocito T (TIL)", macro: "Macrófago", nk: "Célula NK" },
    },
  },
  vascular: {
    title_bg: "Съдова мрежа — ендотел & перицити",
    title_en: "Vascular network — endothelium & pericytes",
    title_es: "Red vascular — endotelio & pericitos",
    description_bg: "Капиляри в гръдната тъкан — ендотелните клетки регулират ангиогенезата. Перицитите стабилизират съдовете.",
    description_en: "Breast tissue capillaries — endothelial cells regulate angiogenesis. Pericytes stabilise vessels.",
    labels: {
      bg: { endo: "Ендотелна\nклетка", peri: "Перицит", rbc: "Еритроцит" },
      en: { endo: "Endothelial cell", peri: "Pericyte", rbc: "Erythrocyte" },
      es: { endo: "Célula endotelial", peri: "Pericito", rbc: "Eritrocito" },
    },
  },
  lymphatic: {
    title_bg: "Лимфна мрежа — съдове, възли и дренаж",
    title_en: "Lymphatic network — vessels, nodes, drainage",
    title_es: "Red linfática — vasos, ganglios y drenaje",
    description_bg: "Лимфните капиляри събират интерстициалната течност и я насочват към аксиларните възли (75% от лимфата на гърдата). Имунният надзор се извършва именно в лимфните възли. Движението и мускулната помпа задвижват лимфния поток — лимфната система няма собствено сърце.",
    description_en: "Lymphatic capillaries collect interstitial fluid and drain it toward the axillary nodes (75% of breast lymph). Immune surveillance happens in lymph nodes. Movement and muscle pump drive the flow — the lymphatic system has no heart of its own.",
    description_es: "Los capilares linfáticos recogen el líquido intersticial y lo drenan hacia los ganglios axilares (75% de la linfa mamaria). La vigilancia inmune ocurre en los ganglios linfáticos.",
    labels: {
      bg: { vessel: "Лимфен\nкапиляр", node: "Аксиларен\nлимфен възел", valve: "Едностранна\nклапа", fluid: "Интерстициална\nтечност" },
      en: { vessel: "Lymphatic capillary", node: "Axillary lymph node", valve: "One-way valve", fluid: "Interstitial fluid" },
      es: { vessel: "Capilar linfático", node: "Ganglio axilar", valve: "Válvula unidireccional", fluid: "Líquido intersticial" },
    },
  },
};


export default function CytologyZoom({ zone, onClose, lang = "bg" }) {
  const [pathologyMode, setPathologyMode] = useState(false);
  const [stage, setStage] = useState("normal");
  if (!zone) return null;
  const content = CYTOLOGY_CONTENT[zone];
  if (!content) return null;
  const labels = content.labels[lang] || content.labels.bg;
  const title = content[`title_${lang}`] || content.title_bg;
  const description = content[`description_${lang}`] || content.description_bg;
  const showPathology = pathologyMode && zone === "epithelium";

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[9999] flex items-start sm:items-center justify-center bg-black/70 backdrop-blur-md p-2 sm:p-4 overflow-y-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid="cytology-zoom-modal"
      >
        <motion.div
          className="relative max-w-4xl w-full bg-white dark:bg-[hsl(222,47%,11%)] rounded-2xl overflow-hidden shadow-2xl my-4 sm:my-0 max-h-[95vh] flex flex-col"
          initial={{ scale: 0.92, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.92, y: 20 }}
          transition={{ type: "spring", stiffness: 200, damping: 24 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-5 border-b border-gray-200/30 dark:border-white/10 flex items-start justify-between gap-3 shrink-0">
            <div>
              <div className="text-xs opacity-60 mb-0.5">
                {lang === "en" ? "Microscopic view" : lang === "es" ? "Vista microscópica" : "Микроскопски изглед"}
              </div>
              <h2 className="text-lg font-bold">{title}</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10"
              data-testid="cytology-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Scrollable body */}
          <div className="overflow-y-auto flex-1">
          {/* SVG canvas */}
          <div className="p-5 bg-gradient-to-br from-pink-50 to-purple-50 dark:from-pink-500/10 dark:to-purple-500/10">
            <svg viewBox="0 0 760 420" className="w-full h-auto block">
              {showPathology
                ? <PathologySVG stage={stage} stageData={PATHOLOGY_STAGES[stage]} />
                : <CytologySVG zone={zone} labels={labels} />
              }
            </svg>
          </div>

          {/* Pathology Mode toggle — epithelium only */}
          {zone === "epithelium" && (
            <div className="px-5 pb-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold opacity-70">🔬 Pathology Mode (Образователно сравнение)</span>
                <button
                  onClick={() => setPathologyMode(!pathologyMode)}
                  className={`text-xs px-3 py-1 rounded-full font-semibold transition-colors ${
                    pathologyMode
                      ? "bg-pink-600 text-white"
                      : "bg-gray-200 dark:bg-white/10 text-gray-700 dark:text-gray-300"
                  }`}
                  data-testid="pathology-mode-toggle"
                >
                  {pathologyMode ? "ON" : "OFF"}
                </button>
              </div>
              {pathologyMode && (
                <PathologyStageSlider stage={stage} onChange={setStage} />
              )}
            </div>
          )}

          {/* Description */}
          <div className="p-5">
            {showPathology && (
              <div className="mb-3">
                <div className="text-xs font-bold text-pink-600 dark:text-pink-300 mb-0.5" data-testid="pathology-stage-title">
                  {PATHOLOGY_STAGES[stage].title_bg}
                </div>
                <div className="text-[11px] opacity-70 italic">
                  {PATHOLOGY_STAGES[stage].risk_bg}
                </div>
              </div>
            )}
            <p className="text-sm leading-relaxed" data-testid="cytology-description">
              {showPathology ? PATHOLOGY_STAGES[stage].desc_bg : description}
            </p>
            {showPathology && (
              <div className="mt-3 p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/30 flex items-start gap-2 text-[11px]">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-rose-500" />
                <span><b>Не е диагноза.</b> Образователно сравнение на стадии — НЕ е резултат от Вашата биопсия. Не замества медицински преглед.</span>
              </div>
            )}
            <div className="mt-3 flex items-center gap-2 text-[11px] opacity-60">
              <ZoomOut className="w-3.5 h-3.5" />
              {lang === "en" ? "Educational visualisation — not actual microscopy."
               : lang === "es" ? "Visualización educativa — no es microscopía real."
               : "Образователна визуализация — не е реална микроскопия."}
            </div>
          </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}


function PathologyStageSlider({ stage, onChange }) {
  const idx = PATHOLOGY_STAGE_KEYS.indexOf(stage);
  const total = PATHOLOGY_STAGE_KEYS.length;
  return (
    <div className="select-none" data-testid="pathology-stage-slider">
      {/* Slider track */}
      <div className="relative pt-1 pb-1">
        <input
          type="range"
          min={0}
          max={total - 1}
          step={1}
          value={idx < 0 ? 0 : idx}
          onChange={(e) => onChange(PATHOLOGY_STAGE_KEYS[Number(e.target.value)])}
          className="w-full h-2 appearance-none rounded-full cursor-pointer accent-pink-600
                     bg-gradient-to-r from-emerald-400 via-amber-400 via-orange-500 to-rose-600"
          data-testid="pathology-stage-range"
          aria-label="Стадий на патологично прогресиране"
          style={{ background: "linear-gradient(90deg,#10B981 0%,#F59E0B 35%,#F97316 60%,#EF4444 80%,#9F1239 100%)" }}
        />
        {/* Tick markers */}
        <div className="flex justify-between mt-2 px-0.5">
          {PATHOLOGY_STAGE_KEYS.map((key, i) => (
            <button
              key={key}
              onClick={() => onChange(key)}
              className={`flex flex-col items-center gap-1 flex-1 ${i === idx ? "text-pink-600 dark:text-pink-300 font-bold" : "opacity-50 hover:opacity-100"}`}
              data-testid={`pathology-stage-${key}`}
            >
              <span className={`w-2 h-2 rounded-full ${i === idx ? "bg-pink-600 scale-150" : "bg-current"}`} />
              <span className="text-[10px] text-center leading-tight">
                {PATHOLOGY_STAGES[key].title_bg.split(" (")[0]}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}


function PathologySVG({ stage, stageData }) {
  // Educational stylized cross-section of a TDLU at the given stage
  const { cellRows = 1, cellsPerRow = 8, nucleusSize = 6, atypia = false, bm = true, filled = false } = stageData || {};
  const cx = 380, cy = 210;
  const rx = 260, ry = 160;
  // Stage progression colour (green → red)
  const stageColours = { normal: "#10B981", hyperplasia: "#F59E0B", adh: "#F97316", dcis: "#EF4444", invasive: "#9F1239" };
  const stageColor = stageColours[stage] || "#EC4899";
  // Build concentric rings of cells (one ring per cellRows level)
  const rings = [];
  for (let r = 0; r < cellRows; r++) {
    const ringRx = rx - 20 - r * 32;
    const ringRy = ry - 20 - r * 22;
    for (let i = 0; i < cellsPerRow + r * 2; i++) {
      const angle = (i / (cellsPerRow + r * 2)) * 2 * Math.PI;
      const x = cx + Math.cos(angle) * ringRx;
      const y = cy + Math.sin(angle) * ringRy;
      // Nuclei pleomorphism for atypia (variable size + dark)
      const nucR = atypia ? nucleusSize + ((i + r) % 3) - 1 : nucleusSize;
      const fill = atypia ? "#831843" : "#FBCFE8";
      const nucFill = atypia ? "#1E1B4B" : "#9D174D";
      rings.push(
        <g key={`${r}-${i}`}>
          <ellipse cx={x} cy={y} rx={20 - r * 1.5} ry={24 - r * 2} fill={fill} stroke="#EC4899" strokeWidth="1" />
          <circle cx={x} cy={y - 2} r={nucR} fill={nucFill} />
        </g>
      );
    }
  }

  // Invasive: cells breaking through BM into stroma
  const invasiveCells = stage === "invasive" ? (
    <g>
      {[[110, 90],[640, 110],[120, 340],[660, 320]].map(([x, y], i) => (
        <g key={i}>
          <ellipse cx={x} cy={y} rx="14" ry="18" fill="#831843" stroke="#1E1B4B" strokeWidth="1.5" />
          <circle cx={x} cy={y - 1} r="7" fill="#1E1B4B" />
        </g>
      ))}
      {/* Arrows showing escape direction */}
      {[[210, 130, 130, 100],[550, 130, 620, 105],[210, 290, 130, 330],[550, 290, 620, 315]].map(([x1, y1, x2, y2], i) => (
        <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#9F1239" strokeWidth="2" strokeDasharray="4 2" markerEnd="url(#arr)" />
      ))}
      <defs>
        <marker id="arr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 Z" fill="#9F1239" />
        </marker>
      </defs>
    </g>
  ) : null;

  return (
    <g>
      {/* Basement membrane (broken if invasive) */}
      {bm ? (
        <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="none" stroke="#9333EA" strokeWidth="3" strokeDasharray="6 3" />
      ) : (
        <>
          {/* Disrupted BM: alternating segments */}
          <path
            d={`M ${cx - rx} ${cy} A ${rx} ${ry} 0 0 1 ${cx + rx} ${cy}`}
            fill="none" stroke="#9333EA" strokeWidth="3" strokeDasharray="20 18" opacity="0.7"
          />
          <path
            d={`M ${cx - rx} ${cy} A ${rx} ${ry} 0 0 0 ${cx + rx} ${cy}`}
            fill="none" stroke="#9333EA" strokeWidth="3" strokeDasharray="20 22" opacity="0.5"
          />
        </>
      )}
      {/* Cells */}
      {rings}
      {/* Lumen */}
      <ellipse
        cx={cx} cy={cy}
        rx={filled ? 14 : 140 - cellRows * 12}
        ry={filled ? 8 : 60 - cellRows * 8}
        fill={filled ? "#831843" : "#FEF3C7"}
        stroke={filled ? "#1E1B4B" : "#F59E0B"}
        strokeWidth="1"
      />
      {!filled && (
        <text x={cx} y={cy + 5} textAnchor="middle" style={{ fontSize: 12, fontWeight: 700, fill: "#92400E" }}>
          Лумен
        </text>
      )}
      {invasiveCells}
      {/* Stage indicator badge */}
      <g transform="translate(60, 50)">
        <rect x="-4" y="-22" rx="12" ry="12" width="180" height="28" fill={stageColor} opacity="0.15" stroke={stageColor} strokeWidth="1.5" />
        <circle cx="14" cy="-8" r="6" fill={stageColor} />
        <text x="28" y="-3" style={{ fontSize: 12, fontWeight: 800, fill: stageColor }}>
          {stage.toUpperCase()}
        </text>
      </g>
    </g>
  );
}


function CytologySVG({ zone, labels }) {
  if (zone === "epithelium") {
    // TDLU: ductal lumen + luminal epithelium + myoepithelial layer + basement membrane
    return (
      <g>
        {/* Basement membrane (outermost ring) */}
        <ellipse cx="380" cy="210" rx="260" ry="160" fill="none" stroke="#9333EA" strokeWidth="3" strokeDasharray="6 3" />
        {/* Myoepithelial layer */}
        <ellipse cx="380" cy="210" rx="240" ry="142" fill="rgba(168, 85, 247, 0.18)" />
        {/* Luminal epithelial cells (cuboidal) */}
        {Array.from({ length: 18 }).map((_, i) => {
          const angle = (i / 18) * 2 * Math.PI;
          const cx = 380 + Math.cos(angle) * 200;
          const cy = 210 + Math.sin(angle) * 110;
          return (
            <g key={i}>
              <ellipse cx={cx} cy={cy} rx="22" ry="26" fill="#FBCFE8" stroke="#EC4899" strokeWidth="1.2" />
              <circle cx={cx} cy={cy - 3} r="6" fill="#9D174D" />
            </g>
          );
        })}
        {/* Lumen (central) */}
        <ellipse cx="380" cy="210" rx="140" ry="60" fill="#FEF3C7" stroke="#F59E0B" strokeWidth="1" />
        <text x="380" y="215" textAnchor="middle" style={{ fontSize: 13, fontWeight: 700, fill: "#92400E" }}>{labels.lumen}</text>
        {/* Annotations */}
        <Label x="100" y="60"  text={labels.basement}     color="#9333EA" />
        <Label x="100" y="200" text={labels.myoepithelial} color="#A855F7" />
        <Label x="100" y="350" text={labels.luminal}      color="#DB2777" />
      </g>
    );
  }
  if (zone === "stroma") {
    return (
      <g>
        {/* ECM fibrous background */}
        {Array.from({ length: 24 }).map((_, i) => (
          <line key={i}
            x1={50 + i * 30} y1={40 + (i % 3) * 20}
            x2={120 + i * 30} y2={380 - (i % 3) * 30}
            stroke={i % 2 ? "#7C3AED" : "#A78BFA"}
            strokeWidth={i % 3 === 0 ? 2.5 : 1.5}
            opacity={0.55}
          />
        ))}
        {/* Fibroblasts (spindle-shaped) */}
        {[[200, 150],[480, 220],[330, 320],[600, 130]].map(([cx, cy], i) => (
          <g key={i} transform={`rotate(${15 + i * 30} ${cx} ${cy})`}>
            <ellipse cx={cx} cy={cy} rx="42" ry="11" fill="#5B21B6" opacity="0.85" />
            <circle  cx={cx} cy={cy} r="6" fill="#FBCFE8" />
          </g>
        ))}
        <Label x="40"  y="40"  text={labels.col1}  color="#A78BFA" />
        <Label x="40"  y="380" text={labels.col3}  color="#7C3AED" />
        <Label x="600" y="60"  text={labels.fibro} color="#5B21B6" />
        <Label x="600" y="380" text={labels.ecm}   color="#9333EA" />
      </g>
    );
  }
  if (zone === "adipose") {
    return (
      <g>
        {/* Large adipocytes — round cells with peripheral nuclei */}
        {[[200, 150, 70],[400, 110, 80],[580, 200, 65],[280, 300, 75],[520, 320, 70]].map(([cx, cy, r], i) => (
          <g key={i}>
            <circle cx={cx} cy={cy} r={r} fill="#FDE68A" stroke="#F59E0B" strokeWidth="1.5" />
            <circle cx={cx} cy={cy} r={r - 8} fill="#FEF3C7" />
            <ellipse cx={cx + r - 14} cy={cy + r - 14} rx="8" ry="5" fill="#7C2D12" />
          </g>
        ))}
        {/* Aromatase markers (pink dots near adipocytes) */}
        {[[270, 180],[470, 150],[350, 280]].map(([cx, cy], i) => (
          <circle key={i} cx={cx} cy={cy} r="6" fill="#EC4899" opacity="0.9" />
        ))}
        <Label x="40"  y="60"  text={labels.adipo} color="#92400E" />
        <Label x="40"  y="370" text={labels.lipid} color="#F59E0B" />
        <Label x="620" y="60"  text={labels.arom}  color="#EC4899" />
      </g>
    );
  }
  if (zone === "immune") {
    return (
      <g>
        {/* Tissue background */}
        <rect x="40" y="40" width="680" height="340" rx="20" fill="#FCE7F3" opacity="0.5" />
        {/* T cells */}
        {[[180, 130],[380, 180],[280, 280],[500, 240],[600, 130]].map(([cx, cy], i) => (
          <g key={i}>
            <circle cx={cx} cy={cy} r="22" fill="#60A5FA" stroke="#1E40AF" strokeWidth="1.5" />
            <circle cx={cx} cy={cy} r="11" fill="#1E3A8A" />
          </g>
        ))}
        {/* Macrophages */}
        {[[320, 110],[450, 320],[600, 280]].map(([cx, cy], i) => (
          <g key={i}>
            <path d={`M ${cx} ${cy} m -28 0 a 28 28 0 1 0 56 0 a 28 28 0 1 0 -56 0`}
              fill="#34D399" stroke="#065F46" strokeWidth="1.5" />
            <circle cx={cx - 6} cy={cy - 4} r="3" fill="#065F46" />
            <circle cx={cx + 6} cy={cy + 6} r="3" fill="#065F46" />
          </g>
        ))}
        <Label x="40"  y="380" text={labels.til}   color="#1E40AF" />
        <Label x="350" y="380" text={labels.macro} color="#065F46" />
        <Label x="620" y="60"  text={labels.nk}    color="#1E40AF" />
      </g>
    );
  }
  if (zone === "vascular") {
    return (
      <g>
        {/* Capillary lumen — long tube */}
        <path d="M 60 210 Q 200 180 380 210 T 700 210" stroke="#DC2626" strokeWidth="48" fill="none" opacity="0.25" strokeLinecap="round" />
        {/* Endothelial cells lining the lumen */}
        {Array.from({ length: 12 }).map((_, i) => (
          <ellipse key={i}
            cx={80 + i * 55}
            cy={200 + Math.sin(i) * 12}
            rx="22" ry="14"
            fill="#FCA5A5" stroke="#DC2626" strokeWidth="1.2"
          />
        ))}
        {/* Erythrocytes (RBCs) */}
        {[[180, 210],[300, 215],[450, 205],[580, 210]].map(([cx, cy], i) => (
          <g key={i}>
            <circle cx={cx} cy={cy} r="10" fill="#EF4444" />
            <circle cx={cx} cy={cy} r="3.5" fill="#7F1D1D" />
          </g>
        ))}
        {/* Pericyte (outside) */}
        <ellipse cx="500" cy="280" rx="36" ry="14" fill="#831843" />
        <Label x="40"  y="60"  text={labels.endo} color="#B91C1C" />
        <Label x="40"  y="370" text={labels.rbc}  color="#7F1D1D" />
        <Label x="620" y="370" text={labels.peri} color="#831843" />
      </g>
    );
  }
  if (zone === "lymphatic") {
    return (
      <g>
        {/* Tissue background */}
        <rect x="40" y="40" width="680" height="340" rx="20" fill="#ECFDF5" opacity="0.55" />
        {/* Interstitial fluid dots */}
        {Array.from({ length: 30 }).map((_, i) => (
          <circle key={i}
            cx={70 + (i * 23) % 660}
            cy={70 + ((i * 47) % 280)}
            r="2.5" fill="#34D399" opacity="0.55"
          />
        ))}
        {/* Lymphatic capillary — dashed tube with valves */}
        <path d="M 70 220 Q 220 180 380 210 T 700 210" stroke="#10B981" strokeWidth="22" fill="none" opacity="0.35" strokeLinecap="round" />
        <path d="M 70 220 Q 220 180 380 210 T 700 210" stroke="#059669" strokeWidth="2" fill="none" strokeDasharray="6 4" />
        {/* One-way valves (V-shaped) */}
        {[200, 360, 520].map((cx, i) => (
          <g key={i}>
            <path d={`M ${cx - 10} 198 L ${cx} 215 L ${cx + 10} 198`} stroke="#047857" strokeWidth="2" fill="none" strokeLinecap="round" />
            <path d={`M ${cx - 10} 228 L ${cx} 215 L ${cx + 10} 228`} stroke="#047857" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.55" />
          </g>
        ))}
        {/* Lymph node (axillary) — kidney-bean shape */}
        <g transform="translate(640, 110)">
          <path d="M -36 -18 Q -42 0 -22 18 Q 6 26 30 12 Q 44 -4 24 -22 Q 0 -32 -36 -18 Z"
                fill="#34D399" stroke="#065F46" strokeWidth="2" opacity="0.85" />
          {/* Lymphocytes inside */}
          {[[-18, -6],[-6, 4],[8, -4],[18, 6],[0, 12]].map(([dx, dy], i) => (
            <circle key={i} cx={dx} cy={dy} r="4" fill="#065F46" />
          ))}
        </g>
        {/* Labels */}
        <Label x="40"  y="60"  text={labels.vessel} color="#059669" />
        <Label x="40"  y="370" text={labels.fluid}  color="#10B981" />
        <Label x="350" y="370" text={labels.valve}  color="#047857" />
        <Label x="600" y="60"  text={labels.node}   color="#065F46" />
      </g>
    );
  }
  return null;
}

function Label({ x, y, text, color }) {
  const lines = (text || "").split("\n");
  return (
    <g>
      {lines.map((line, i) => (
        <text
          key={i}
          x={x}
          y={y + i * 14}
          style={{ fontSize: 11, fontWeight: 700, fill: color || "#374151" }}
        >
          {line}
        </text>
      ))}
    </g>
  );
}
